// FinKloz — Bank Reconciliation Panel (Full-Width Orchestrator)
// Lives inside the Cash & Cash Equivalents section.
// Tab bar: Summary + one tab per bank account + Template + Export.

import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, amber, amberLight, useTheme } from '../tokens';
import useBankRecState from '../hooks/useBankRecState';
import BankRecAccountTab from './BankRecAccountTab';
import CashTemplateSummary from './CashTemplateSummary';
import { BANK_ACCOUNTS } from '../services/bankStatementParser';
import { buildCashTemplate } from '../services/cashTemplateBuilder';
import { exportCashTemplate } from '../services/excelExporter';

const teal = '#0D9488';
const tealLight = '#CCFBF1';

// ─── Status dot for tab bar ───
function StatusDot({ status }) {
  const color = status === 'completed' || status === 'reviewed' ? green
    : status === 'matched' ? amber
    : status === 'ready_to_reconcile' || status === 'statement_uploaded' || status === 'gl_uploaded' ? '#7C3AED'
    : '#D1D5DB';
  return (
    <span style={{
      display: 'inline-block', width: 7, height: 7,
      borderRadius: '50%', background: color,
      marginRight: 4,
    }} />
  );
}

export default function BankRecPanel({ companyId, month, glMappings, onAccountReconciled }) {
  const t = useTheme();
  const { state, dispatch, saveNow } = useBankRecState(companyId, month);

  const activeAccount = state.activeAccount || 'summary';
  const tabBarRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = tabBarRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState);
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateScrollState); ro.disconnect(); };
  }, [updateScrollState]);

  // Resolve GL accounts: prop > chart of accounts > month-specific TB
  const resolvedGLAccounts = useMemo(() => {
    if (glMappings?.accounts?.length > 0) return glMappings.accounts;
    try {
      // Try the month-agnostic chart of accounts first
      const coa = localStorage.getItem('finkloz_chart_of_accounts');
      if (coa) {
        const accounts = JSON.parse(coa);
        if (accounts?.length > 0) return accounts;
      }
      // Fall back to any month-specific TB import
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('finkloz_gl_mappings_')) {
          const data = JSON.parse(localStorage.getItem(key));
          if (data?.accounts?.length > 0) return data.accounts;
        }
      }
    } catch { /* ignore */ }
    return [];
  }, [glMappings]);

  // Build template data from current state
  const templateData = useMemo(() => {
    return buildCashTemplate(state, BANK_ACCOUNTS);
  }, [state]);

  // Handle export
  const handleExport = useCallback(() => {
    try {
      exportCashTemplate(templateData);
    } catch (err) {
      console.error('Export failed:', err);
    }
  }, [templateData]);

  // Count completed accounts
  const completedCount = Object.values(state.accounts).filter(a =>
    a.status === 'completed' || a.status === 'reviewed' || a.status === 'matched'
  ).length;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${t.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontFamily: sans, fontSize: 15, fontWeight: 700, color: t.textPrimary }}>
            Bank Reconciliation
          </div>
          <div style={{ fontFamily: sans, fontSize: 11, color: t.textSecondary, marginTop: 2 }}>
            {month} -- {completedCount}/{BANK_ACCOUNTS.length} accounts reconciled
          </div>
        </div>
        {/* Download button only visible on summary/template tabs */}
      </div>

      {/* Tab bar — scrollable with arrow indicators */}
      <div style={{ position: 'relative', flexShrink: 0, borderBottom: `1px solid ${t.border}` }}>
        {/* Left scroll arrow */}
        {canScrollLeft && (
          <button onClick={() => { tabBarRef.current.scrollBy({ left: -120, behavior: 'smooth' }); }}
            style={{ position: 'absolute', left: 0, top: 0, bottom: 1, zIndex: 2, width: 28, border: 'none', cursor: 'pointer', background: `linear-gradient(to right, ${t.bgCard} 60%, transparent)`, color: t.textMuted, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: 6 }}>
            &#8249;
          </button>
        )}
        {/* Right scroll arrow */}
        {canScrollRight && (
          <button onClick={() => { tabBarRef.current.scrollBy({ left: 120, behavior: 'smooth' }); }}
            style={{ position: 'absolute', right: 0, top: 0, bottom: 1, zIndex: 2, width: 28, border: 'none', cursor: 'pointer', background: `linear-gradient(to left, ${t.bgCard} 60%, transparent)`, color: t.textMuted, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 6 }}>
            &#8250;
          </button>
        )}
      <div ref={tabBarRef} style={{
        display: 'flex', gap: 1, padding: '0 8px',
        overflowX: 'auto', scrollbarWidth: 'none',
        flexShrink: 0,
      }}>
        {/* Summary tab */}
        <button
          onClick={() => dispatch({ type: 'SET_ACTIVE_ACCOUNT', accountNo: 'summary' })}
          style={{
            fontFamily: sans, fontSize: 11, fontWeight: activeAccount === 'summary' ? 700 : 400,
            padding: '8px 12px', border: 'none', cursor: 'pointer',
            borderBottom: activeAccount === 'summary' ? `2px solid ${teal}` : '2px solid transparent',
            background: 'transparent',
            color: activeAccount === 'summary' ? teal : t.textSecondary,
            whiteSpace: 'nowrap',
          }}
        >
          Summary
        </button>

        {/* Per-account tabs */}
        {BANK_ACCOUNTS.map(acct => {
          const acctState = state.accounts[acct.accountNo];
          const isActive = activeAccount === acct.accountNo;
          return (
            <button
              key={acct.accountNo}
              onClick={() => dispatch({ type: 'SET_ACTIVE_ACCOUNT', accountNo: acct.accountNo })}
              style={{
                fontFamily: sans, fontSize: 11, fontWeight: isActive ? 700 : 400,
                padding: '8px 10px', border: 'none', cursor: 'pointer',
                borderBottom: isActive ? `2px solid ${teal}` : '2px solid transparent',
                background: 'transparent',
                color: isActive ? teal : t.textSecondary,
                display: 'flex', alignItems: 'center', gap: 2,
                whiteSpace: 'nowrap',
              }}
            >
              <StatusDot status={acctState?.status} />
              <span>{acct.accountNo}</span>
              <span style={{ fontSize: 9, color: t.textMuted, marginLeft: 2 }}>{acct.currency}</span>
            </button>
          );
        })}

        {/* Template tab */}
        <button
          onClick={() => dispatch({ type: 'SET_ACTIVE_ACCOUNT', accountNo: 'template' })}
          style={{
            fontFamily: sans, fontSize: 11, fontWeight: activeAccount === 'template' ? 700 : 400,
            padding: '8px 12px', border: 'none', cursor: 'pointer',
            borderBottom: activeAccount === 'template' ? `2px solid ${teal}` : '2px solid transparent',
            background: 'transparent',
            color: activeAccount === 'template' ? teal : t.textSecondary,
            whiteSpace: 'nowrap',
          }}
        >
          Template
        </button>
      </div>
      </div>

      {/* Tab content */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '12px 16px',
      }}>
        {activeAccount === 'summary' && (
          <div>
            <CashTemplateSummary templateData={templateData} />
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button
                onClick={handleExport}
                style={{
                  fontFamily: sans, fontSize: 12, fontWeight: 600,
                  padding: '8px 18px', borderRadius: 6,
                  border: `1px solid ${t.border}`, background: t.bgSurface, color: t.textPrimary,
                  cursor: 'pointer',
                }}
              >
                Download for Auditor
              </button>
            </div>
          </div>
        )}

        {activeAccount === 'template' && (
          <div>
            <CashTemplateSummary templateData={templateData} />
            <div style={{ marginTop: 20, textAlign: 'center' }}>
              <button
                onClick={handleExport}
                style={{
                  fontFamily: sans, fontSize: 13, fontWeight: 600,
                  padding: '10px 24px', borderRadius: 6,
                  border: `1px solid ${teal}`, background: teal, color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Download Excel Workbook
              </button>
              <div style={{ fontFamily: sans, fontSize: 11, color: t.textSecondary, marginTop: 6 }}>
                Generates the full bank reconciliation workbook for audit review.
              </div>
            </div>
          </div>
        )}

        {activeAccount !== 'summary' && activeAccount !== 'template' && (() => {
          const acctConfig = BANK_ACCOUNTS.find(a => a.accountNo === activeAccount);
          const acctData = state.accounts[activeAccount];
          if (!acctConfig) return null;
          return (
            <div>
              <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: t.textPrimary, marginBottom: 2 }}>
                {acctConfig.name}
              </div>
              <div style={{ fontFamily: sans, fontSize: 11, color: t.textSecondary, marginBottom: 12 }}>
                GL Account: {acctConfig.glAccount} | Currency: {acctConfig.currency} | Bank: {acctConfig.bank}
              </div>
              <BankRecAccountTab
                accountNo={activeAccount}
                accountData={acctData}
                dispatch={dispatch}
                accountConfig={acctConfig}
                glAccounts={resolvedGLAccounts}
                companyId={companyId}
                month={month}
                saveNow={saveNow}
                onAccountReconciled={onAccountReconciled}
              />
            </div>
          );
        })()}
      </div>
    </div>
  );
}
